import React from 'react';
import { get, filter, map, find, cloneDeep, isUndefined, isEmpty } from 'lodash';

// React view component to render
import { memberInfoSettingShippingInfo as MemberInfoSettingShippingInfoView } from 'ui/views/widgets/memberInfoSettingShippingInfo';
import { MemberInfoSettingShippingInfoService } from 'services/memberInfoSettingShippingInfo';
import * as confirmOverlay from 'utils/confirmOverlay';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { getLabel } from 'utils/getLabel';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION, GLOBAL_LABELS, SIZES, BLANK_VALUE_SYMBOL } from 'constants/common';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberInfoSettingShippingInfo } from 'ui/containers/MemberInfoSettingShippingInfo';
 *
 * // render
 * <MemberInfoSettingShippingInfo propName='propValue'/>
 */
export class MemberInfoSettingShippingInfo extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind function
        this._bind();

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            showCustomErrorView: false
        };

        // Member Detail instance
        this.memberInfoSettingShippingInfoService = new MemberInfoSettingShippingInfoService();

        // start member detail data fetch
        this.fetchComponentData();
    }

    _bind() {
        this.handleAddressInfoUpdate = this.handleAddressInfoUpdate.bind( this );
        this.updateAddressSelection = this.updateAddressSelection.bind( this );
        this.handleSetDefaultAction = this.handleSetDefaultAction.bind( this );
        this.handleDeleteAction = this.handleDeleteAction.bind( this );
        this.handleInactivateAction = this.handleInactivateAction.bind( this );
        this.handleActivateAction = this.handleActivateAction.bind( this );

    }

    /**
     * @desc: fetch component data
     */
    async fetchComponentData() {
        log.debug( 'MemberInfoSettingShippingInfo.fetchComponentData()' );

        // cancel ongoing request
        this.memberInfoSettingShippingInfoService.cancel();

        // fetch component data
        const { status, data } = await this.memberInfoSettingShippingInfoService.fetchComponentData( {
            url: this.props.widget.url,
            data: this.props.requestPayload
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            if( ! isEmpty( get( data, 'description', {} ) ) ) {
                this.setState( {
                    isFetching: false,
                    showCustomErrorView: true,
                    componentData: data

                    // networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            }
        } else {

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data
            } );
        }
    }

    /**
     * @desc: called when user updates the currect address selection
     */
    updateAddressSelection( { address } ) {
        log.debug( 'MemberInfoSettingShippingInfo.updateAddressSelection()' );

        const componentData = cloneDeep( this.state.componentData );
        const updatedList = map( componentData.addresses, item => {
            item.selected = item.id === address.id ? true: false;
            return item;
        } );
        componentData.addresses = updatedList;

        this.setState( { componentData } );

    }

    /**
     * @desc: called when user updates the currect address selection
     */
    async handleSetDefaultAction( { address } ) {
        log.debug( 'MemberInfoSettingShippingInfo.handleSetDefaultAction()' );

        // cancel ongoing request
        this.memberInfoSettingShippingInfoService.cancel();

        //show preloader
        pageLevelPreloader.show();

        const url = find( this.state.componentData.modifyActions, { name: 'setDefault' } )[ 'url' ];

        // set the address default
        const { status, data } = await this.memberInfoSettingShippingInfoService.setDefaultAddress( {
            url,
            data: {
                context: {
                    ...get( this.props, 'requestPayload.context', {} )
                },
                data: {
                    id: address.id
                }
            }
        } );

        //hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update address list
            const componentData = cloneDeep( this.state.componentData );
            const updatedList = map( componentData.addresses, item => {
                item.flags.isDefault = address.id === item.id ? true: false;
                return item;
            } );
            componentData.addresses = updatedList;
    
            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                isFetching: true
            }, () => {

                // Fetch component data again to sync
                // inactivate flags from impl: 40683
                this.fetchComponentData();

                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
            } );


        } else {
            
            // this.setState( {
            //     networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            // }, () => {

            //     // show error dialog
            //     if( ! isEmpty( get( data, 'description' ) ) ) {
            //         errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            //     } else {
            //         errorOverlay.open();
            //     }

            // } );

            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc: action executed when MSR clicks on delete action
     */
    async handleDeleteAction( { address } ) {
        log.debug( 'MemberInfoSettingShippingInfo.handleDeleteAction()' );

        // cancel ongoing request
        this.memberInfoSettingShippingInfoService.cancel();

        const isDefault = get( address, 'flags.isDefault', false );

        // get delete action
        const action = find( this.state.componentData.modifyActions, { name: 'delete' } );

        if( ! isDefault ) {

            const addressOption = get( this.state, 'componentData.options.addressType', [] );
            const type = find( addressOption, { value: address.type } );

            const shouldProceed = await confirmOverlay.open( {
                message: getLabel( get( action, 'confirmation.message', {} ) ),
                confirmViewConfig: {
                    type: 'address',
                    address: {
                        type: isUndefined( type ) ? BLANK_VALUE_SYMBOL: getLabel( type.title ),
                        value: filter( [ address.line1, address.line2, address.city, address.state, address.zip ], Boolean ).join( ', ' )
                    }
                },
                iconName: 'close'
            } );

            if( shouldProceed ) {

                //show preloader
                pageLevelPreloader.show();

                // set the address default
                const { status, data } = await this.memberInfoSettingShippingInfoService.deleteSavedAddress( {
                    url: action.url,
                    data: {
                        context: {
                            operation: CRUD_OPERATION.DELETE,
                            ...get( this.props, 'requestPayload.context', {} )
                        },
                        data: {
                            id: address.id
                        }
                    },
                } );

                //show preloader
                pageLevelPreloader.hide();

                if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                    // update address list
                    const componentData = cloneDeep( this.state.componentData );
                    const updatedList = filter( componentData.addresses, item => {
                        return item.id !== address.id;
                    } );
                    componentData.addresses = updatedList;

                    this.setState( {
                        componentData,
                        networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
                    } );

                } else {
                    
                    // this.setState( {
                    //     updatingItems: [],
                    //     networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                    // }, () => {
                        
                    //     // show error dialog
                    //     if( ! isEmpty( get( data, 'description' ) ) ) {
                    //         errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
                    //     } else {
                    //         errorOverlay.open();
                    //     }
                        
                    // } );

                    // show error dialog
                    if( ! isEmpty( get( data, 'description' ) ) ) {
                        errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
                    } else {
                        errorOverlay.open();
                    }
                }
            }
        } else {

            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }

        
    }

    /**
     * @desc: action executed when MSR clicks on inactivate action
     */
    async handleInactivateAction( { address } ) {
        log.debug( 'MemberInfoSettingShippingInfo.handleInactivateAction()' );

        // cancel ongoing request
        this.memberInfoSettingShippingInfoService.cancel();

        const isDefault = get( address, 'flags.isDefault', false );

        // get Inactivate action
        const action = find( this.state.componentData.modifyActions, { name: 'inactivate' } );

        if( ! isDefault ) {

            const addressOption = get( this.state, 'componentData.options.addressType', [] );
            const type = find( addressOption, { value: address.type } );

            const shouldProceed = await confirmOverlay.open( {
                message: getLabel( get( action, 'confirmation.message', {} ) ),
                confirmViewConfig: {
                    type: 'address',
                    address: {
                        type: isUndefined( type ) ? BLANK_VALUE_SYMBOL: getLabel( type.title ),
                        value: filter( [ address.line1, address.line2, address.city, address.state, address.zip ], Boolean ).join( ', ' )
                    }
                },
                iconName: 'close'
            } );

            if( shouldProceed ) {

                //show preloader
                pageLevelPreloader.show();

                // set the address default
                const { status, data } = await this.memberInfoSettingShippingInfoService.inactivateSavedAddress( {
                    url: action.url,
                    data: {
                        context: {
                            ...get( this.props, 'requestPayload.context', {} )
                        },
                        data: {
                            id: address.id,
                            type: address.type,
                            line1: address.line1,
                            line2: address.line2,
                            city: address.city,
                            state: address.state,
                            zip: address.zip,
                            //Disabled for sprint 37
                           defaultBillAddress: address.defaultBillAddress,
                            defaultShipAddress: address.defaultShipAddress
                        }
                    },
                } );

                //show preloader
                pageLevelPreloader.hide();

                if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                    // update address list
                    const componentData = cloneDeep( this.state.componentData );
                    const updatedItem = find( componentData.addresses, { id: address.id  } );
           
                    updatedItem.flags.isActivated = false;

                    this.setState( {
                        componentData,
                        networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
                    } );

                } else {
                    
                    // show error dialog
                    if( ! isEmpty( get( data, 'description' ) ) ) {
                        errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
                    } else {
                        errorOverlay.open();
                    }
                }
            }
        } else {

            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }

        
    }

    /**
     * @desc: action executed when MSR clicks on activate action
     */
    async handleActivateAction( { address } ) {
        log.debug( 'MemberInfoSettingShippingInfo.handleActivateAction()' );

        // cancel ongoing request
        this.memberInfoSettingShippingInfoService.cancel();

        // get activate action
        const action = find( this.state.componentData.modifyActions, { name: 'activate' } );

        //show preloader
        pageLevelPreloader.show();

        // set the address default
        const { status, data } = await this.memberInfoSettingShippingInfoService.activateSavedAddress( {
            url: action.url,
            data: {
                context: {
                    ...get( this.props, 'requestPayload.context', {} )
                },
                data: {
                    id: address.id,
                    type: address.type,
                    line1: address.line1,
                    line2: address.line2,
                    city: address.city,
                    state: address.state,
                    zip: address.zip,
                    //Disabled for sprint37
                    defaultBillAddress: address.defaultBillAddress,
                    defaultShipAddress: address.defaultShipAddress
                }
            },
        } );

        //show preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update address list
            const componentData = cloneDeep( this.state.componentData );
            const updatedItem = find( componentData.addresses, { id: address.id  } );
           
            updatedItem.flags.isActivated = true;

            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            } );

        } else {
            
            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
        
    }

    async handleAddressInfoUpdate( { type, address, id = null } ) {
        log.debug( 'MemberInfoSettingShippingInfo.handleAddressInfoUpdate()' );

        // get action
        const action = get( this.state, 'componentData.addAction', {} );

        // cancel ongoing request
        this.memberInfoSettingShippingInfoService.cancel();
        
        // show page preloader
        pageLevelPreloader.show();

        const formData = {
            ...address,
            id: id,
        };

        if( 'edit' === type ) {
            const selectedAddress = find( this.state.componentData.addresses, { id: id } );
            formData.isDefault = ! isUndefined( selectedAddress ) ? get( selectedAddress, 'flags.isDefault' ): null;
          
             //Disabled for sprint37
             formData.defaultBillAddress =  selectedAddress.defaultBillAddress? selectedAddress.defaultBillAddress:false;
            formData.defaultShipAddress =  selectedAddress.defaultShipAddress? selectedAddress.defaultShipAddress:false;
       
        //   //test Added
        //     formData.isDefaultShipping=formData.isDefault?true:false;
        //     formData.isDefaultBilling=formData.isDefault?false:true;
        }

         // set the address default
        const { status, data } = await this.memberInfoSettingShippingInfoService.submitSelectedAddress( {
            url: get( action, 'config.submitUrl', {} ),
            data: {
                context: {
                    operation: 'add' === type ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE,
                    ...get( this.props, 'requestPayload.context', {} )
                },
                data: formData
            }
        } );

        // show page preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                isFetching: true
            }, () => {

                // fetch new list
                this.fetchComponentData();

            } );

        } else {

            // this.setState( {
            //     networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            // }, () => {
                
            //     // show error dialog
            //     if( ! isEmpty( get( data, 'description' ) ) ) {
            //         errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            //     } else {
            //         errorOverlay.open();
            //     }

            // } );

            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }

    }

    /**
     * @desc When component unmounts, cleanup resources.
     */
    componentWillUnmount() {
        this.memberInfoSettingShippingInfoService.cancel(); // cancel ongoing requests
    }

    /******************************************** */
    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberInfoSettingShippingInfoView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} )}
                requestPayload={ this.props.requestPayload }
                onAddressSelection={ this.updateAddressSelection }
                onDefaultSelection={ this.handleSetDefaultAction }
                onAddressDelete={ this.handleDeleteAction }
                onAddressActivate={ this.handleActivateAction }
                onAddressInactivate={ this.handleInactivateAction }
                onAddEditAddressClick={ this.handleAddressInfoUpdate }
                showCustomErrorView={ this.state.showCustomErrorView }
            />
        );
    }
}

// set display name
MemberInfoSettingShippingInfo.displayName = 'MemberInfoSettingShippingInfo';

// set default props
MemberInfoSettingShippingInfo.defaultProps = {};

// prop types
MemberInfoSettingShippingInfo.propTypes = {};
